import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Download, Link2, ShieldAlert, Trash2, Upload, X, XCircle } from "lucide-react";
import { API, api, formatApiError } from "../lib/api";
import { BUOY_STATUS, DAMM, dammInfo, FLODESSTATUS, LAYERS, NODE_TYPES, rollStale } from "../lib/constants";
import { BottleneckDialog, inputCls, labelCls } from "./Modals";

const ENTITY_KIND = { node: "nod", relation: "relation", flow: "flode" };

function SourceList({ sources, entityType, entityId, refresh }) {
    const [linkId, setLinkId] = useState("");
    const linked = sources.filter((s) => (s.lankar || []).some((l) => l.entity_type === entityType && l.entity_id === entityId));

    const link = async () => {
        if (!linkId) return;
        try {
            await api.post(`/sources/${linkId}/link`, { entity_type: entityType, entity_id: entityId });
            toast.success("Källa länkad");
            setLinkId("");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div>
            <p className={labelCls}>Empirisk förankring</p>
            {linked.length === 0 && <p className="text-xs text-slate-500 mb-2">Ingen källa länkad ännu.</p>}
            <div className="space-y-2 mb-2">
                {linked.map((s) => (
                    <div key={s.id} data-testid={`linked-source-${s.id}`} className="rounded-lg border border-border bg-abyss/40 p-2.5">
                        <div className="text-xs font-semibold text-slate-200">{s.titel}</div>
                        <div className="font-mono text-[9px] uppercase tracking-widest text-slate-500">{s.typ} · {s.datum}</div>
                        {s.utdrag && <p className="text-[11px] text-slate-400 italic mt-1 leading-relaxed">"{s.utdrag}"</p>}
                        {s.konflikt && (
                            <p data-testid={`conflict-badge-${s.id}`} className="text-[10px] text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5 mt-1.5 inline-block">
                                Konflikterande vittnesmål{s.konflikt_med ? ` – mot "${s.konflikt_med}"` : ""}
                            </p>
                        )}
                    </div>
                ))}
            </div>
            <div className="flex gap-2">
                <select data-testid="link-source-select" className={`${inputCls} text-xs`} value={linkId} onChange={(e) => setLinkId(e.target.value)}>
                    <option value="">Välj källa att länka …</option>
                    {sources.filter((s) => !linked.some((l) => l.id === s.id)).map((s) => (
                        <option key={s.id} value={s.id}>{s.titel}</option>
                    ))}
                </select>
                <button data-testid="link-source-button" onClick={link} disabled={!linkId}
                    className="shrink-0 text-current hover:text-tide border border-current/30 rounded-lg px-2.5 disabled:opacity-40 transition-colors">
                    <Link2 className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

function DammRow({ b, refresh }) {
    const info = dammInfo(b);
    const setStatus = async (status) => {
        await api.put(`/bottlenecks/${b.id}`, { status });
        toast.success(status === "bekraftad" ? "Damm bekräftad" : "Damm avvisad");
        refresh();
    };
    const remove = async () => {
        await api.delete(`/bottlenecks/${b.id}`);
        refresh();
    };
    return (
        <div data-testid={`damm-row-${b.id}`} className="rounded-lg border p-2.5" style={{ borderColor: `${info.color}55`, background: `${info.color}0d` }}>
            <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold" style={{ color: info.color }}>{info.label}</span>
                <div className="flex items-center gap-1">
                    {b.status === "foreslagen" ? (
                        <>
                            <span data-testid={`damm-status-${b.id}`} className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mr-1">
                                {b.kalla === "ai" ? "AI-förslag" : "Förslag"}
                            </span>
                            <button data-testid={`damm-confirm-${b.id}`} onClick={() => setStatus("bekraftad")} title="Bekräfta"
                                className="text-buoy hover:text-emerald-300 transition-colors"><CheckCircle2 className="w-4 h-4" /></button>
                            <button data-testid={`damm-reject-${b.id}`} onClick={() => setStatus("avvisad")} title="Avvisa"
                                className="text-slate-500 hover:text-red-400 transition-colors"><XCircle className="w-4 h-4" /></button>
                        </>
                    ) : (
                        <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: info.color }}>
                            {b.status === "bekraftad" ? "Bekräftad" : b.status}
                        </span>
                    )}
                    <button data-testid={`damm-delete-${b.id}`} onClick={remove} className="text-slate-600 hover:text-red-400 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
            <div className="flex gap-0.5 mt-1.5">
                {[1, 2, 3, 4, 5].map((v) => (
                    <span key={v} className="h-1 flex-1 rounded-full" style={{ background: v <= b.allvarlighet ? info.color : "#1E293B" }} />
                ))}
            </div>
            {b.notering && <p className="text-[11px] text-slate-400 mt-1.5 leading-relaxed">{b.notering}</p>}
        </div>
    );
}

export default function DetailPanel({ selection, graph, projectId, refresh, onClose }) {
    const [dammOpen, setDammOpen] = useState(false);
    const [edit, setEdit] = useState(null);

    const entity = (() => {
        if (!selection || !graph) return null;
        const { kind, id } = selection;
        if (kind === "node") return graph.nodes.find((n) => n.id === id);
        if (kind === "relation") return graph.relations.find((r) => r.id === id);
        if (kind === "flow") return graph.flows.find((f) => f.id === id);
        if (kind === "bottleneck") return graph.bottlenecks.find((b) => b.id === id);
        if (kind === "buoy") return graph.buoys.find((b) => b.id === id);
        return null;
    })();

    useEffect(() => {
        if (!entity || !selection) { setEdit(null); return; }
        if (selection.kind === "node") setEdit({ label: entity.label, description: entity.description || "", perspectives: entity.perspectives || [], subtype: entity.subtype || "", antal_anstallda: entity.antal_anstallda ?? "", varde: entity.varde || "", kostnad: entity.kostnad ?? "", roller: entity.roller || [], uppgifter: entity.uppgifter || [], andamal: entity.andamal || "", kritikalitet: entity.kritikalitet ?? "", algoritm_ai: entity.algoritm_ai || false, utsatthet_algoritm: entity.utsatthet_algoritm ?? "", reviderad: entity.reviderad || "" });
        else if (selection.kind === "relation") setEdit({ label: entity.label || "", perspective: entity.perspective, description: entity.description || "" });
        else if (selection.kind === "flow") setEdit({ label: entity.label || "", volume: entity.volume, tempo: entity.tempo, flodesstatus: entity.flodesstatus || "nar_fram" });
        else setEdit(null);
    }, [selection?.kind, selection?.id, graph]);

    if (!selection || !entity) {
        return (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-6" data-testid="detail-empty">
                <ShieldAlert className="w-8 h-8 text-slate-700 mb-3" />
                <p className="text-sm text-slate-500 leading-relaxed">
                    Klicka på en nod, relation, ett flöde eller en damm i kartan för att se detaljer och empirisk förankring.
                </p>
            </div>
        );
    }

    const saveNode = async () => {
        const payload = { ...edit };
        for (const key of ["antal_anstallda", "kritikalitet", "utsatthet_algoritm", "kostnad"]) {
            if (payload[key] === "") payload[key] = null;
            else if (payload[key] != null) payload[key] = Number(payload[key]);
        }
        payload.roller = (payload.roller || []).map((r) => ({ ...r, kostnad: r.kostnad === "" || r.kostnad == null ? null : Number(r.kostnad) }));
        payload.uppgifter = payload.uppgifter || [];
        try {
            await api.put(`/nodes/${entity.id}`, payload);
            toast.success("Nod uppdaterad");
            refresh();
        } catch (err) {
            toast.error(formatApiError(err, "Kunde inte spara noden"));
        }
    };
    const uploadDoc = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post(`/nodes/${entity.id}/document`, fd);
            toast.success("Dokument uppladdat");
            refresh();
        } catch (err) {
            toast.error(formatApiError(err, "Uppladdningen misslyckades"));
        }
        e.target.value = "";
    };
    const saveRelation = async () => {
        try {
            await api.put(`/relations/${entity.id}`, { source: entity.source, target: entity.target, ...edit });
            toast.success("Relation uppdaterad");
            refresh();
        } catch (err) {
            toast.error(formatApiError(err, "Kunde inte spara relationen"));
        }
    };
    const saveFlow = async () => {
        try {
            await api.put(`/flows/${entity.id}`, { source: entity.source, target: entity.target, label: edit.label, volume: Number(edit.volume), tempo: Number(edit.tempo), flodesstatus: edit.flodesstatus });
            toast.success("Flöde uppdaterat");
            refresh();
        } catch (err) {
            toast.error(formatApiError(err, "Kunde inte spara flödet"));
        }
    };

    const updateRoll = (i, patch) => setEdit((e) => ({ ...e, roller: e.roller.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
    const addRoll = () => setEdit((e) => ({ ...e, roller: [...(e.roller || []), { id: crypto.randomUUID(), namn: "", beskrivning: "", kostnad: "", reviderad: "", rapporterar_till: [], aven_i: [] }] }));
    const removeRoll = (i) => setEdit((e) => ({ ...e, roller: e.roller.filter((_, j) => j !== i) }));
    const toggleRollRef = (i, key, id) => setEdit((e) => ({
        ...e,
        roller: e.roller.map((r, j) => {
            if (j !== i) return r;
            const list = r[key] || [];
            return { ...r, [key]: list.includes(id) ? list.filter((x) => x !== id) : [...list, id] };
        }),
    }));
    const updateUppgift = (i, patch) => setEdit((e) => ({ ...e, uppgifter: e.uppgifter.map((u, j) => (j === i ? { ...u, ...patch } : u)) }));
    const addUppgift = () => setEdit((e) => ({ ...e, uppgifter: [...(e.uppgifter || []), { id: crypto.randomUUID(), namn: "" }] }));
    const removeUppgift = (i) => setEdit((e) => ({ ...e, uppgifter: e.uppgifter.filter((_, j) => j !== i) }));
    const removeEntity = async () => {
        const map = { node: "nodes", relation: "relations", flow: "flows", buoy: "buoys" };
        await api.delete(`/${map[selection.kind]}/${entity.id}`);
        toast.success("Borttagen");
        onClose();
        refresh();
    };

    const nodeLabel = (nid) => graph.nodes.find((n) => n.id === nid)?.label || "?";
    const kind = selection.kind;
    const entityType = ENTITY_KIND[kind];
    const damsHere = kind === "node" || kind === "flow"
        ? graph.bottlenecks.filter((b) => b.target_type === entityType && b.target_id === entity.id && b.status !== "avvisad")
        : [];
    const avdelningDams = kind === "node" && entity.type === "avdelning" ? (() => {
        const flowIds = graph.flows.filter((f) => f.source === entity.id || f.target === entity.id).map((f) => f.id);
        return graph.bottlenecks.filter((b) => b.status === "bekraftad" &&
            ((b.target_type === "flode" && flowIds.includes(b.target_id)) || (b.target_type === "nod" && b.target_id === entity.id)));
    })() : [];

    return (
        <div className="flex-1 overflow-y-auto panel-scroll p-4 space-y-5" data-testid="detail-panel">
            <div className="flex items-start justify-between">
                <div>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">
                        {{ node: NODE_TYPES[entity.type]?.label, relation: "Relation", flow: "Flöde", bottleneck: "Damm", buoy: "Boj" }[kind]}
                    </p>
                    <h3 className="font-display font-bold text-lg text-slate-100 leading-snug">{entity.label || entity.titel || entity.etikett || dammInfo(entity).label}</h3>
                </div>
                <button data-testid="detail-close-button" onClick={onClose} className="text-slate-600 hover:text-slate-300 transition-colors">
                    <X className="w-4 h-4" />
                </button>
            </div>

            {kind === "node" && entity.type === "avdelning" && (entity.antal_anstallda || entity.varde || entity.kostnad != null || avdelningDams.length > 0) && (
                <div className="rounded-xl border border-border bg-abyss/40 p-3 space-y-1.5" data-testid="avdelning-kapacitet">
                    <div className="flex items-center gap-3 flex-wrap">
                        {entity.antal_anstallda != null && (
                            <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                                {entity.antal_anstallda} anställda
                            </span>
                        )}
                        {entity.kostnad != null && (
                            <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                                ≈ {entity.kostnad} tkr/år
                            </span>
                        )}
                        {avdelningDams.length > 0 && (
                            <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-amber-400">
                                <AlertTriangle className="w-3 h-3" />
                                {avdelningDams.length} bekräftade dammar påverkar avdelningen
                            </span>
                        )}
                    </div>
                    {entity.varde && <p className="text-[11px] text-slate-400 leading-relaxed"><span className="text-slate-500">Värde:</span> {entity.varde}</p>}
                </div>
            )}

            {kind === "node" && edit && (
                <div className="space-y-3">
                    <div>
                        <label className={labelCls}>Namn</label>
                        <input data-testid="node-label-input" className={inputCls} value={edit.label} onChange={(e) => setEdit({ ...edit, label: e.target.value })} />
                    </div>
                    <div>
                        <label className={labelCls}>{entity.type === "roll" ? "Rollbeskrivning" : "Beskrivning"}</label>
                        <textarea data-testid="node-desc-input" className={inputCls} rows={2} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} />
                    </div>
                    {entity.type === "avdelning" && (
                        <>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelCls}>Antal anställda</label>
                                    <input data-testid="node-headcount-input" type="number" min="0" className={inputCls}
                                        value={edit.antal_anstallda} onChange={(e) => setEdit({ ...edit, antal_anstallda: e.target.value })} placeholder="T.ex. 12" />
                                </div>
                                <div>
                                    <label className={labelCls}>Årskostnad (tkr)</label>
                                    <input data-testid="node-kostnad-input" type="number" min="0" className={inputCls}
                                        value={edit.kostnad} onChange={(e) => setEdit({ ...edit, kostnad: e.target.value })} placeholder="T.ex. 8400" />
                                </div>
                            </div>
                            <div>
                                <label className={labelCls}>Roller i avdelningen</label>
                                <div className="space-y-2">
                                    {(edit.roller || []).map((r, i) => (
                                        <div key={r.id} className="rounded-lg border border-border bg-abyss/40 p-2.5 space-y-2" data-testid={`roll-editor-${i}`}>
                                            <div className="flex gap-2">
                                                <input className={inputCls} placeholder="Rollnamn" value={r.namn} data-testid={`roll-namn-${i}`}
                                                    onChange={(e) => updateRoll(i, { namn: e.target.value })} />
                                                <button onClick={() => removeRoll(i)} title="Ta bort roll" data-testid={`roll-remove-${i}`}
                                                    className="text-slate-600 hover:text-red-400 shrink-0 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                                            </div>
                                            <textarea className={inputCls} rows={2} placeholder="Rollbeskrivning" value={r.beskrivning}
                                                onChange={(e) => updateRoll(i, { beskrivning: e.target.value })} />
                                            <div className="grid grid-cols-2 gap-2">
                                                <input type="number" min="0" className={inputCls} placeholder="Årskostnad (tkr)" value={r.kostnad ?? ""}
                                                    onChange={(e) => updateRoll(i, { kostnad: e.target.value })} data-testid={`roll-kostnad-${i}`} />
                                                <input className={inputCls} placeholder="Reviderad ÅÅÅÅ-MM" value={r.reviderad || ""}
                                                    onChange={(e) => updateRoll(i, { reviderad: e.target.value })} />
                                            </div>
                                            <div>
                                                <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mb-1">Rapporterar till (flera val = dubbelarbete-risk)</p>
                                                <div className="flex flex-wrap gap-1">
                                                    {graph.nodes.filter((n) => n.type === "avdelning").map((a) => (
                                                        <button key={a.id} type="button" data-testid={`roll-reportsto-${i}-${a.id}`}
                                                            onClick={() => toggleRollRef(i, "rapporterar_till", a.id)}
                                                            className={`text-[10px] rounded-full border px-2 py-0.5 transition-colors ${(r.rapporterar_till || []).includes(a.id) ? "border-current/60 text-current bg-current/10" : "border-border text-slate-500 hover:text-slate-300"}`}>
                                                            {a.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            <div>
                                                <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mb-1">Arbetar även i (gränsöverskridande)</p>
                                                <div className="flex flex-wrap gap-1">
                                                    {graph.nodes.filter((n) => n.type === "avdelning" && n.id !== entity.id).map((a) => (
                                                        <button key={a.id} type="button" data-testid={`roll-aveni-${i}-${a.id}`}
                                                            onClick={() => toggleRollRef(i, "aven_i", a.id)}
                                                            className={`text-[10px] rounded-full border px-2 py-0.5 transition-colors ${(r.aven_i || []).includes(a.id) ? "border-tide/60 text-tide bg-tide/10" : "border-border text-slate-500 hover:text-slate-300"}`}>
                                                            {a.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <button data-testid="add-roll" onClick={addRoll}
                                        className="w-full rounded-lg border border-dashed border-border text-slate-500 py-1.5 text-xs hover:border-current/50 hover:text-current transition-colors">
                                        + Lägg till roll
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className={labelCls}>Uppgifter</label>
                                <div className="space-y-1.5">
                                    {(edit.uppgifter || []).map((u, i) => (
                                        <div key={u.id} className="flex gap-2">
                                            <input className={inputCls} placeholder="Uppgift" value={u.namn} data-testid={`uppgift-namn-${i}`}
                                                onChange={(e) => updateUppgift(i, { namn: e.target.value })} />
                                            <button onClick={() => removeUppgift(i)} title="Ta bort uppgift" data-testid={`uppgift-remove-${i}`}
                                                className="text-slate-600 hover:text-red-400 shrink-0 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                                        </div>
                                    ))}
                                    <button data-testid="add-uppgift" onClick={addUppgift}
                                        className="w-full rounded-lg border border-dashed border-border text-slate-500 py-1.5 text-xs hover:border-current/50 hover:text-current transition-colors">
                                        + Lägg till uppgift
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className={labelCls}>Avdelningens värde för organisationen</label>
                                <textarea data-testid="node-value-input" className={inputCls} rows={2} value={edit.varde}
                                    onChange={(e) => setEdit({ ...edit, varde: e.target.value })}
                                    placeholder="Vad bidrar avdelningen med? Vad händer om den inte levererar?" />
                            </div>
                        </>
                    )}
                    <div>
                        <label className={labelCls}>Perspektivlager</label>
                        <div className="flex flex-wrap gap-1.5">
                            {LAYERS.map((l) => (
                                <button key={l.id} data-testid={`node-persp-${l.id}`}
                                    onClick={() => setEdit({ ...edit, perspectives: edit.perspectives.includes(l.id) ? edit.perspectives.filter((p) => p !== l.id) : [...edit.perspectives, l.id] })}
                                    className={`text-[11px] rounded-full border px-2.5 py-1 transition-all ${edit.perspectives.includes(l.id) ? "font-semibold" : "text-slate-500"}`}
                                    style={edit.perspectives.includes(l.id) ? { borderColor: l.color, color: l.color, background: `${l.color}14` } : { borderColor: "#1E293B" }}>
                                    {l.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    {entity.type === "roll" && (
                        <>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelCls}>Årskostnad (tkr)</label>
                                <input data-testid="node-rollkostnad-input" type="number" min="0" className={inputCls}
                                    value={edit.kostnad} onChange={(e) => setEdit({ ...edit, kostnad: e.target.value })} placeholder="T.ex. 620" />
                            </div>
                        </div>
                        <div>
                            <label className={labelCls}>Rollens värde för organisationen</label>
                            <textarea data-testid="node-rollvarde-input" className={inputCls} rows={2} value={edit.varde}
                                onChange={(e) => setEdit({ ...edit, varde: e.target.value })}
                                placeholder="Vad genererar rollen? Vad händer om den uteblir?" />
                        </div>
                        <div>
                            <label className={labelCls}>Senast reviderad (ÅÅÅÅ-MM)</label>
                            <input data-testid="node-reviderad-input" className={inputCls} value={edit.reviderad || ""}
                                placeholder="2026-05" onChange={(e) => setEdit({ ...edit, reviderad: e.target.value })} />
                            {rollStale(edit.reviderad) && (
                                <p data-testid="roll-stale-warning" className="flex items-center gap-1.5 text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg px-2.5 py-1.5 mt-2">
                                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                                    Oreviderad roll (>12 mån) – riskerar att fokusera på fel saker.
                                </p>
                            )}
                        </div>
                        </>
                    )}
                    {entity.type === "dokument" && (
                        <div>
                            <label className={labelCls}>Dokumenttyp</label>
                            <select data-testid="node-subtype-select" className={inputCls} value={edit.subtype || ""}
                                onChange={(e) => setEdit({ ...edit, subtype: e.target.value })}>
                                <option value="">Välj …</option>
                                <option value="arbetsbeskrivning">Arbetsbeskrivning</option>
                                <option value="styrdokument">Styrdokument</option>
                                <option value="ovrigt">Övrigt</option>
                            </select>
                        </div>
                    )}
                    {entity.type === "system" && (
                        <>
                            <div>
                                <label className={labelCls}>Ändamål – vad ska systemet bidra med?</label>
                                <textarea data-testid="node-andamal-input" className={inputCls} rows={2} value={edit.andamal || ""}
                                    onChange={(e) => setEdit({ ...edit, andamal: e.target.value })}
                                    placeholder="Hur hjälper systemet organisationen att nå sina mål?" />
                            </div>
                            <div>
                                <label className={labelCls}>Kritikalitet ({edit.kritikalitet || "–"}/5)</label>
                                <div className="flex gap-1.5">
                                    {[1, 2, 3, 4, 5].map((v) => (
                                        <button key={v} data-testid={`node-kritikalitet-${v}`} type="button" onClick={() => setEdit({ ...edit, kritikalitet: v })}
                                            className={`flex-1 rounded-lg border py-1.5 text-xs font-semibold transition-all ${Number(edit.kritikalitet) >= v ? "bg-current/20 border-current/60 text-current" : "border-border text-slate-500"}`}>
                                            {v}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <label className="flex items-center gap-2 text-sm text-slate-400">
                                <input data-testid="node-algoritm-check" type="checkbox" checked={!!edit.algoritm_ai}
                                    onChange={(e) => setEdit({ ...edit, algoritm_ai: e.target.checked })} className="accent-fuchsia-400" />
                                Innehåller algoritm/AI som påverkar människor
                            </label>
                            {edit.algoritm_ai && (
                                <div>
                                    <label className={labelCls}>Mänsklig utsatthet ({edit.utsatthet_algoritm || "–"}/5)</label>
                                    <div className="flex gap-1.5">
                                        {[1, 2, 3, 4, 5].map((v) => (
                                            <button key={v} data-testid={`node-utsatthet-${v}`} type="button" onClick={() => setEdit({ ...edit, utsatthet_algoritm: v })}
                                                className={`flex-1 rounded-lg border py-1.5 text-xs font-semibold transition-all ${Number(edit.utsatthet_algoritm) >= v ? "bg-fuchsia-500/20 border-fuchsia-500/60 text-fuchsia-300" : "border-border text-slate-500"}`}>
                                                {v}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    <button data-testid="node-save-button" onClick={saveNode} className="w-full bg-current/15 text-current border border-current/40 rounded-lg py-2 text-sm font-semibold hover:bg-current/25 transition-colors">
                        Spara ändringar
                    </button>
                </div>
            )}

            {kind === "node" && entity.type === "dokument" && (
                <div>
                    <p className={labelCls}>Dokumentfil</p>
                    {entity.dok_filnamn ? (
                        <div className="flex items-center justify-between rounded-lg border border-border bg-abyss/40 px-3 py-2 mb-2">
                            <span className="text-xs text-slate-300 truncate">{entity.dok_filnamn}</span>
                            <a data-testid="node-doc-download" href={`${API}/nodes/${entity.id}/document`}
                                className="text-current hover:text-tide transition-colors shrink-0 ml-2" title="Ladda ner">
                                <Download className="w-4 h-4" />
                            </a>
                        </div>
                    ) : (
                        <p className="text-xs text-slate-500 mb-2">Ingen fil uppladdad.</p>
                    )}
                    <label data-testid="node-doc-upload-label"
                        className="flex items-center justify-center gap-2 border border-dashed border-border rounded-lg py-2.5 text-xs text-slate-400 hover:border-current/40 hover:text-current cursor-pointer transition-colors">
                        <Upload className="w-4 h-4" />
                        {entity.dok_filnamn ? "Ersätt fil" : "Ladda upp fil (PDF, Word, bild)"}
                        <input data-testid="node-doc-upload" type="file" className="sr-only" onChange={uploadDoc} />
                    </label>
                </div>
            )}

            {kind === "relation" && edit && (
                <div className="space-y-3">
                    <p className="text-xs text-slate-400">{nodeLabel(entity.source)} → {nodeLabel(entity.target)}</p>
                    <div>
                        <label className={labelCls}>Etikett</label>
                        <input data-testid="relation-label-input" className={inputCls} value={edit.label} onChange={(e) => setEdit({ ...edit, label: e.target.value })} />
                    </div>
                    <div>
                        <label className={labelCls}>Perspektiv</label>
                        <select data-testid="relation-persp-select" className={inputCls} value={edit.perspective} onChange={(e) => setEdit({ ...edit, perspective: e.target.value })}>
                            {LAYERS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                        </select>
                    </div>
                    <button data-testid="relation-save-button" onClick={saveRelation} className="w-full bg-current/15 text-current border border-current/40 rounded-lg py-2 text-sm font-semibold hover:bg-current/25 transition-colors">
                        Spara ändringar
                    </button>
                </div>
            )}

            {kind === "flow" && edit && (
                <div className="space-y-3">
                    <p className="text-xs text-slate-400">{nodeLabel(entity.source)} → {nodeLabel(entity.target)}</p>
                    <div>
                        <label className={labelCls}>Beskrivning</label>
                        <input data-testid="flow-label-edit" className={inputCls} value={edit.label} onChange={(e) => setEdit({ ...edit, label: e.target.value })} />
                    </div>
                    <div>
                        <label className={labelCls}>Informationsstatus</label>
                        <select data-testid="flow-status-edit" className={inputCls} value={edit.flodesstatus} onChange={(e) => setEdit({ ...edit, flodesstatus: e.target.value })}>
                            {Object.entries(FLODESSTATUS).map(([id, s]) => <option key={id} value={id}>{s.label}</option>)}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>Volym ({edit.volume}/5)</label>
                            <input data-testid="flow-volume-edit" type="range" min="1" max="5" value={edit.volume} onChange={(e) => setEdit({ ...edit, volume: e.target.value })} className="w-full accent-sky-400" />
                        </div>
                        <div>
                            <label className={labelCls}>Tempo ({edit.tempo}/5)</label>
                            <input data-testid="flow-tempo-edit" type="range" min="1" max="5" value={edit.tempo} onChange={(e) => setEdit({ ...edit, tempo: e.target.value })} className="w-full accent-sky-400" />
                        </div>
                    </div>
                    <button data-testid="flow-save-button" onClick={saveFlow} className="w-full bg-current/15 text-current border border-current/40 rounded-lg py-2 text-sm font-semibold hover:bg-current/25 transition-colors">
                        Spara ändringar
                    </button>
                </div>
            )}

            {kind === "bottleneck" && (
                <div className="space-y-3">
                    <DammRow b={entity} refresh={refresh} />
                    <p className="text-xs text-slate-500">
                        Kopplad till: {entity.target_type === "nod" ? nodeLabel(entity.target_id) : (() => {
                            const fl = graph.flows.find((f) => f.id === entity.target_id);
                            return fl ? `${nodeLabel(fl.source)} → ${nodeLabel(fl.target)}` : entity.target_id;
                        })()}
                    </p>
                </div>
            )}

            {kind === "buoy" && (
                <div className="space-y-2">
                    <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border"
                        style={{ color: BUOY_STATUS[entity.status]?.color, borderColor: `${BUOY_STATUS[entity.status]?.color}55`, background: `${BUOY_STATUS[entity.status]?.color}14` }}>
                        {BUOY_STATUS[entity.status]?.label || entity.status}
                    </span>
                    <p className="text-xs text-slate-400 leading-relaxed">
                        Hantera materialuppladdning, av-identifiering och analys i fliken <span className="text-slate-200 font-medium">Bojar</span>.
                    </p>
                    {entity.insights && (
                        <div className="rounded-lg border border-buoy/30 bg-buoy/5 p-3">
                            <p className="font-mono text-[9px] uppercase tracking-widest text-buoy mb-1">Systemnivåinsikt</p>
                            <p className="text-xs text-slate-300 leading-relaxed">{entity.insights.sammanfattning}</p>
                            <div className="flex flex-wrap gap-1 mt-2">
                                {(entity.insights.teman || []).map((t, i) => (
                                    <span key={i} className="text-[10px] bg-buoy/10 text-buoy border border-buoy/30 rounded-full px-2 py-0.5">{t}</span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {(kind === "node" || kind === "flow") && (
                <div>
                    <p className={labelCls}>Dammar här ({damsHere.length})</p>
                    <div className="space-y-2 mb-2">
                        {damsHere.map((b) => <DammRow key={b.id} b={b} refresh={refresh} />)}
                    </div>
                    <button data-testid="create-bottleneck-button" onClick={() => setDammOpen(true)}
                        className="w-full flex items-center justify-center gap-2 border border-amber-500/40 text-amber-400 rounded-lg py-2 text-sm font-medium hover:bg-amber-500/10 transition-colors">
                        <AlertTriangle className="w-4 h-4" /> Sätt damm här
                    </button>
                </div>
            )}

            {entityType && (
                <SourceList sources={graph.sources} entityType={entityType} entityId={entity.id} refresh={refresh} />
            )}

            {kind !== "bottleneck" && (
                <button data-testid="entity-delete-button" onClick={removeEntity}
                    className="w-full flex items-center justify-center gap-2 border border-red-500/30 text-red-400 rounded-lg py-2 text-sm hover:bg-red-500/10 transition-colors">
                    <Trash2 className="w-4 h-4" /> Ta bort
                </button>
            )}

            <BottleneckDialog
                open={dammOpen}
                onClose={() => setDammOpen(false)}
                projectId={projectId}
                target={{ target_type: entityType, target_id: entity.id, label: entity.label }}
                onSaved={refresh}
            />
        </div>
    );
}
